import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { trackSearchCity } from "../lib/track";
import { MapPin, ArrowRight, Navigation, Loader2, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { saveDeliveryInfo, titleCase } from "../lib/checkoutStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LARGE_CITIES = [
  "roma", "milano", "napoli", "torino", "palermo", "genova",
  "bologna", "firenze", "bari", "catania", "venezia", "verona"
];

export default function DeliverySearchBar({ variant = "hero", onFound }) {
  const navigate = useNavigate();
  const [cityQuery, setCityQuery] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [cap, setCap] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [needsCap, setNeedsCap] = useState(false);
  const [detectedCityName, setDetectedCityName] = useState("");
  const [searchPhase, setSearchPhase] = useState("city");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [capLoading, setCapLoading] = useState(false);
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [showStreets, setShowStreets] = useState(false);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState(null);

  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const capDebounceRef = useRef(null);
  const streetDebounceRef = useRef(null);
  const selectedStreetRef = useRef(null);
  const streetsRef = useRef(null);
  const addressInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (streetsRef.current && !streetsRef.current.contains(e.target) &&
          addressInputRef.current && !addressInputRef.current.contains(e.target)) {
        setShowStreets(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/geo/autocomplete`, { params: { q: query } });
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleCityChange = (e) => {
    const val = e.target.value;
    setCityQuery(val);
    setSelectedCity(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    const displayName = suggestion.display_name;
    setCityQuery(displayName);
    setSelectedCity({ lat: suggestion.lat, lon: suggestion.lon, display_name: displayName });
    setShowSuggestions(false);
    setSuggestions([]);

    const isLarge = LARGE_CITIES.some(city => new RegExp(`\\b${city}\\b`, 'i').test(displayName));
    if (isLarge) {
      const foundCity = LARGE_CITIES.find(c => new RegExp(`\\b${c}\\b`, 'i').test(displayName));
      setDetectedCityName(foundCity.charAt(0).toUpperCase() + foundCity.slice(1));
      setNeedsCap(true);
    } else {
      setNeedsCap(false);
      setDetectedCityName("");
    }
    setSearchPhase("address");
  };

  // Auto-suggest CAP when user types address in a large city
  const suggestCap = async (address, city) => {
    if (!address || address.length < 4 || !city) return;
    setCapLoading(true);
    try {
      const res = await axios.get(`${API}/geo/suggest-cap`, { params: { address, city }, timeout: 9000 });
      if (res.data.cap && !selectedStreetRef.current?.postcode) {
        setCap(res.data.cap);
      }
    } catch (err) {
      // silent fail
    } finally {
      setCapLoading(false);
    }
  };

  const cityName = () => detectedCityName || (selectedCity ? selectedCity.display_name.split(",")[0].trim() : cityQuery.split(",")[0].trim());

  const fetchStreets = async (query) => {
    const q = query.replace(/\d+.*$/, "").trim();
    if (q.length < 3) { setStreetSuggestions([]); return; }
    setLoadingStreets(true);
    try {
      const res = await axios.get(`${API}/geo/streets`, {
        params: { q, city: cityName(), lat: selectedCity?.lat, lon: selectedCity?.lon }, timeout: 10000
      });
      setStreetSuggestions(res.data);
      setShowStreets(res.data.length > 0);
    } catch (err) {
      setStreetSuggestions([]);
    } finally {
      setLoadingStreets(false);
    }
  };

  const handleSelectStreet = (st) => {
    selectedStreetRef.current = st;
    setSelectedStreet(st);
    setFullAddress(`${st.name} `);
    setShowStreets(false);
    setStreetSuggestions([]);
    if (capDebounceRef.current) clearTimeout(capDebounceRef.current);
    if (st.postcode) { setCap(st.postcode); setCapLoading(false); }
    else if (needsCap) { fetchCapAt(st.lat, st.lon); }
    setSearchError("");
    setTimeout(() => {
      const el = addressInputRef.current;
      if (el) { el.focus(); const len = el.value.length; el.setSelectionRange(len, len); }
    }, 0);
  };

  const fetchCapAt = async (lat, lon) => {
    setCapLoading(true);
    try {
      const res = await axios.get(`${API}/geo/cap-at`, { params: { lat, lon }, timeout: 8000 });
      if (res.data.cap && selectedStreetRef.current) setCap(res.data.cap);
    } catch (err) { /* silent */ } finally { setCapLoading(false); }
  };

  const handleAddressChange = (e) => {
    const val = e.target.value;
    setFullAddress(val);
    setSearchError("");

    // Street autocomplete (only while typing the street name, before the house number)
    const streetPart = val.replace(/\d+.*$/, "").trim();
    if (selectedStreet && !val.toLowerCase().startsWith(selectedStreet.name.toLowerCase())) {
      setSelectedStreet(null);
      selectedStreetRef.current = null;
    }
    if (!/\d/.test(val) && !(selectedStreet && streetPart.toLowerCase() === selectedStreet.name.toLowerCase())) {
      if (streetDebounceRef.current) clearTimeout(streetDebounceRef.current);
      streetDebounceRef.current = setTimeout(() => fetchStreets(val), 300);
    } else {
      setShowStreets(false);
    }

    // Auto-suggest CAP for large cities after debounce (skip if the selected street already gave it)
    if (needsCap && val.length >= 4 && !(selectedStreet?.postcode)) {
      if (capDebounceRef.current) clearTimeout(capDebounceRef.current);
      capDebounceRef.current = setTimeout(() => suggestCap(val, detectedCityName), 600);
    }
  };

  const handleFinalSearch = async (e) => {
    e.preventDefault();
    if (!fullAddress.trim()) return;
    if (needsCap && cap.length < 5) {
      setSearchError("Il CAP e obbligatorio per questa citta");
      return;
    }

    setSearching(true);
    setSearchError("");
    try {
      // Build full address from city context + street address
      const cityContext = detectedCityName || (selectedCity ? selectedCity.display_name.split(",")[0].trim() : "");
      const payload = {
        address: fullAddress.trim(),
        city: cityContext || undefined,
        cap: needsCap ? cap : undefined,
      };
      // Precise coords from the selected street skip server-side geocoding entirely
      if (selectedStreet) {
        payload.lat = selectedStreet.lat;
        payload.lon = selectedStreet.lon;
        payload.precise = true;
        sessionStorage.setItem("search_coords", JSON.stringify({ lat: selectedStreet.lat, lon: selectedStreet.lon }));
      } else if (selectedCity) {
        payload.lat = parseFloat(selectedCity.lat);
        payload.lon = parseFloat(selectedCity.lon);
        sessionStorage.setItem("search_coords", JSON.stringify({ lat: selectedCity.lat, lon: selectedCity.lon }));
      }
      const res = await axios.post(`${API}/geo/find-nearest`, payload, { timeout: 15000 });
      if (res.data.no_florist) {
        setSearchError(res.data.message);
      } else {
        // Save delivery info for checkout
        const deliveryInfo = {
          address: titleCase(fullAddress.trim()),
          city: cityContext || cityQuery,
          cap: needsCap ? cap : (selectedStreet?.postcode || ""),
          shop_id: res.data.shop_id,
          shop_name: res.data.shop_name,
          is_fallback: res.data.is_fallback,
          delivery_cost: res.data.delivery_cost || null,
          coordinates: res.data.coordinates
        };
        saveDeliveryInfo(deliveryInfo);
        trackSearchCity(deliveryInfo.city, res.data);
        if (res.data.coordinates) {
          sessionStorage.setItem("search_coords", JSON.stringify(res.data.coordinates));
        }
        if (onFound) { onFound(res.data); return; }
        navigate(`/shops/${res.data.shop_id}`);
      }
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        setSearchError("La ricerca sta impiegando troppo. Seleziona la via dai suggerimenti e riprova.");
      } else {
        setSearchError(err.response?.data?.detail || "Errore durante la ricerca. Riprova.");
      }
    } finally {
      setSearching(false);
    }
  };

  const resetSearch = () => {
    setSearchPhase("city");
    setCityQuery("");
    setCap("");
    setFullAddress("");
    setSelectedCity(null);
    setNeedsCap(false);
    setDetectedCityName("");
    setSearchError("");
    setSelectedStreet(null);
    selectedStreetRef.current = null;
    setStreetSuggestions([]);
    setShowStreets(false);
  };

  const isHero = variant === "hero";
  const bgClass = isHero ? "bg-white/10 backdrop-blur-md" : "bg-forest/5";
  const textMuted = isHero ? "text-white/60" : "text-sage";
  const textLabel = isHero ? "text-white text-sm" : "text-forest text-sm";
  const hintClass = isHero ? "text-white/50 text-sm" : "text-sage-light text-sm";

  return (
    <div data-testid="delivery-search-bar">
      <div className={`${bgClass} p-3 rounded-2xl`}>
        {/* Phase 1: City */}
        {searchPhase === "city" && (
          <div className="relative">
            <div className="relative flex-1" ref={inputRef}>
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand z-10" />
              <Input
                type="text"
                placeholder="Dove far arrivare i fiori?"
                value={cityQuery}
                onChange={handleCityChange}
                onFocus={(e) => { suggestions.length > 0 && setShowSuggestions(true); if (window.innerWidth < 768) setTimeout(() => { const top = e.target.getBoundingClientRect().top + window.scrollY - 96; window.scrollTo({ top, behavior: "smooth" }); }, 250); }}
                className="pl-12 py-6 bg-white border-0 text-forest placeholder:text-sage-light rounded-xl text-base shadow-sm focus:ring-2 focus:ring-brand/30"
                data-testid="search-city-input"
                autoComplete="off"
              />
              {loadingSuggestions && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-brand border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div ref={suggestionsRef} className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-cream-dark z-50 max-h-64 overflow-y-auto" data-testid="suggestions-dropdown">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => handleSelectSuggestion(s)}
                    className="w-full text-left px-4 py-3 hover:bg-cream/50 flex items-center gap-3 border-b border-cream-dark/30 last:border-0 transition-colors"
                    data-testid={`suggestion-${i}`}>
                    <Navigation className="h-4 w-4 text-brand shrink-0" />
                    <span className="text-sm text-forest truncate">{s.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Address (+CAP auto-suggested for large cities) */}
        {searchPhase === "address" && (
          <form onSubmit={handleFinalSearch} className="space-y-3" data-testid="address-section">
            <div className={`flex items-center gap-2 ${textLabel}`}>
              <MapPin className="h-4 w-4 text-brand-light" />
              <span className="truncate">{cityQuery}</span>
              <button type="button" onClick={resetSearch}
                className={`${textMuted} hover:opacity-100 text-xs underline ml-auto shrink-0`} data-testid="change-city-btn">Cambia</button>
            </div>
            <div className="relative">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand z-10" />
                <Input
                  ref={addressInputRef}
                  type="text"
                  placeholder="Via e numero civico del destinatario"
                  value={fullAddress}
                  onChange={handleAddressChange}
                  onFocus={() => streetSuggestions.length > 0 && !/\d/.test(fullAddress) && setShowStreets(true)}
                  className="pl-12 py-6 bg-white border-0 text-forest placeholder:text-sage-light rounded-xl text-base shadow-sm focus:ring-2 focus:ring-brand/30"
                  data-testid="search-address-input"
                  autoComplete="off"
                  autoFocus
                />
                {loadingStreets && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-brand border-t-transparent rounded-full" />
                  </div>
                )}
                {selectedStreet && !loadingStreets && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand" data-testid="street-selected-check" />
                )}
              </div>
              {showStreets && streetSuggestions.length > 0 && (
                <div ref={streetsRef} className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-cream-dark z-50 max-h-64 overflow-y-auto" data-testid="street-suggestions-dropdown">
                  {streetSuggestions.map((st, i) => (
                    <button key={i} type="button" onClick={() => handleSelectStreet(st)}
                      className="w-full text-left px-4 py-3 hover:bg-cream/50 flex items-center gap-3 border-b border-cream-dark/30 last:border-0 transition-colors"
                      data-testid={`street-suggestion-${i}`}>
                      <Navigation className="h-4 w-4 text-brand shrink-0" />
                      <span className="text-sm text-forest truncate flex-1">{st.name}{st.locality && st.locality.toLowerCase() !== cityName().toLowerCase() ? <span className="text-sage"> — {st.locality}</span> : null}</span>
                      {st.postcode && <span className="text-xs text-sage tracking-widest shrink-0">{st.postcode}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedStreet && !/\d/.test(fullAddress) && (
              <p className={`${textMuted} text-xs -mt-1`} data-testid="street-hint">Ora aggiungi il numero civico</p>
            )}
            {needsCap && (
              <div data-testid="cap-field">
                <div className="relative">
                  <Input
                    type="text"
                    maxLength={5}
                    placeholder={`CAP di ${detectedCityName}`}
                    value={cap}
                    onChange={(e) => { setCap(e.target.value.replace(/\D/g, "")); setSearchError(""); }}
                    className="py-5 bg-white border-0 text-forest placeholder:text-sage-light rounded-xl text-base shadow-sm focus:ring-2 focus:ring-brand/30 text-center tracking-widest"
                    data-testid="search-cap-input"
                  />
                  {capLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-brand" />
                    </div>
                  )}
                </div>
                <p className={`${textMuted} text-xs mt-1`}>
                  {cap ? `CAP suggerito: ${cap} — puoi modificarlo se necessario` : `Scegli la via dai suggerimenti: il CAP si compila da solo`}
                </p>
              </div>
            )}
            {searchError && (
              <p className="text-red-400 text-sm font-medium" data-testid="search-error">{searchError}</p>
            )}
            <Button type="submit" disabled={searching || !fullAddress.trim()}
              className="w-full bg-brand text-white hover:bg-brand-dark py-6 rounded-xl text-base font-medium shadow-lg" data-testid="search-submit-btn">
              {searching ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cerco il fiorista...</>
              ) : (
                <>Trova il fiorista <ArrowRight className="ml-2 h-5 w-5" /></>
              )}
            </Button>
          </form>
        )}
      </div>
      <p className={`${hintClass} mt-3`}>
        {searchPhase === "city" && "Inserisci la citta di destinazione dei fiori"}
        {searchPhase === "address" && (needsCap ? "Scrivi la via, scegli dai suggerimenti e aggiungi il civico — il CAP si compila da solo" : "Scrivi la via, scegli dai suggerimenti e aggiungi il numero civico")}
      </p>
    </div>
  );
}
